import { useState, useMemo } from "react";
import brendanPhoto from "@/assets/brendan-photo.jpg";
import { Link } from "react-router-dom";
import { Play, Mail, ArrowRight, ChevronDown, Users, Palette, Download, Eye, Building2 } from "lucide-react";
import { CoachCarousel } from "@/components/CoachCarousel";
import { ClubLogoCarousel } from "@/components/ClubLogoCarousel";
import { drills } from "@/data/drills";
import { DrillPreviewCard } from "@/components/DrillPreviewCard";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import { HeroVideoPlayer } from "@/components/HeroVideoPlayer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/Header";
import { HeroVideoUploader } from "@/components/HeroVideoUploader";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useTrafficSource } from "@/hooks/useTrafficSource";

interface WelcomeVideo {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  display_order: number;
  video_type: string;
  captions?: string | null;
}

export default function Welcome() {
  const { toast } = useToast();
  const { user, subscribed } = useAuth();
  const { isInstallable, isInstalled, isIOS, isMobile, promptInstall } = useInstallPrompt();
  
  // Capture traffic source on landing
  useTrafficSource();
  
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSending, setIsSending] = useState(false);
  const [isHuman, setIsHuman] = useState(false);
  
  // Get 6 sample drills for preview - one from each category variety
  const previewDrills = useMemo(() => {
    return drills.slice(0, 6);
  }, []);

  // Fetch hero video
  const { data: heroVideo } = useQuery({
    queryKey: ["hero-video"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("welcome_videos")
        .select("*")
        .eq("video_type", "hero")
        .maybeSingle();

      if (error) throw error;
      return data as WelcomeVideo | null;
    },
  });

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast({
        title: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (!user && !isHuman) {
      toast({
        title: "Please confirm you're not a robot",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    
    try {
      const response = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: contactForm.name.trim(),
          email: contactForm.email.trim(),
          message: contactForm.message.trim(),
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast({
        title: "Message sent!",
        description: "Thanks for reaching out. I'll get back to you soon.",
      });
      
      setContactForm({ name: "", email: "", message: "" });
      setIsHuman(false);
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: "Failed to send message",
        description: "Please try again or email me directly.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <Header />
      <StickyMobileCTA />
      <InstallPrompt />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-field-pattern opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-background" />
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Mobile Install Button - Hidden when already installed */}
            {!isInstalled && (
              <div className="md:hidden mb-3">
                <button 
                  onClick={async () => {
                    if (isInstallable) {
                      await promptInstall();
                    } else if (isIOS) {
                      toast({ title: "Install on iOS", description: "Tap the Share button, then 'Add to Home Screen'" });
                    } else {
                      toast({ title: "Install on Android", description: "Tap the menu (⋮), then 'Add to Home Screen' or 'Install App'" });
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/20 border border-primary/30 text-xs text-primary font-medium hover:bg-primary/30 transition-colors"
                >
                  <Download className="h-3 w-3" />
                  Install App
                </button>
              </div>
            )}
            
            {!subscribed && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <span className="text-sm text-primary font-medium">7-Day Money Back Guarantee</span>
              </div>
            )}
            
            <h1 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-foreground mb-4 tracking-wide text-center">
              <span className="block">OZ</span>
              <span className="block">PERFORMANCE</span>
            </h1>
            
            {/* Same tagline on mobile and desktop, just different text size */}
            <p className="text-lg md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Aussie First Platform • Pro Insights • Smoothest Designer • Cost? One Coffee a month
            </p>
            
            {/* Hero Video */}
            {heroVideo ? (
              <div className="mb-4 max-w-5xl mx-auto rounded-xl overflow-hidden border border-border shadow-2xl">
                <div className="aspect-video bg-black">
                  <HeroVideoPlayer heroVideo={heroVideo} />
                </div>
              </div>
            ) : (
              <div className="mb-4 max-w-5xl mx-auto rounded-xl overflow-hidden border border-border shadow-2xl">
                <div className="aspect-video bg-black flex items-center justify-center">
                  <div className="text-center p-8">
                    <Play className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">Video coming soon</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Hero Video Uploader - Admin only */}
            {user?.email === "brendan.curmi.11@gmail.com" && (
              <div className="mb-10 max-w-5xl mx-auto">
                <HeroVideoUploader heroVideo={heroVideo || null} />
              </div>
            )}
            
            {/* Hide hero CTAs on mobile since we have sticky CTA */}
            <div className="hidden sm:flex flex-col sm:flex-row gap-4 justify-center mb-8">
              {subscribed ? (
                <Link to="/library">
                  <Button size="lg" className="gap-2 text-lg px-8 animate-pulse-glow">
                    Go to Drills
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth">
                    <Button size="lg" className="gap-2 text-lg px-8 animate-pulse-glow">
                      Get Started
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/library">
                    <Button variant="outline" size="lg" className="gap-2 text-lg px-8">
                      Explore Drills
                    </Button>
                  </Link>
                </>
              )}
            </div>
            
            {/* Social Proof - Coach Carousel */}
            <div className="mb-4">
              <CoachCarousel />
            </div>

            {/* Club Logo Carousel */}
            <div className="mb-8">
              <ClubLogoCarousel />
            </div>
            
            {/* Testimonial */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="bg-card/50 backdrop-blur rounded-xl p-6 border border-border/50 relative">
                <div className="absolute -top-2 left-6 text-4xl text-primary/30 font-serif">"</div>
                <blockquote className="text-muted-foreground text-sm leading-relaxed mb-4 pt-2">
                  My sister asked me today to do some session planning for her club, so decided to have a look at this and see how it compared to Sport Session Planner that I currently use. SOOO much easier on both phone and laptop to design sessions. I find sports session planner is so fiddly and it takes me so long to use, coincidentally also had a renewal email today so I'll likely be switching over at some point instead. And you've got so many good drills as well! You've done amazing with it. I'm sure you've probably had a lot of compliments but just thought I'd let you know that it's a big tick from me and I'll definitely be referring friends to it too.
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-display text-sm">A</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">Adelyn Ayton</p>
                    <p className="text-xs text-muted-foreground">Football Coach</p>
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => scrollToSection("features")}
              className="animate-bounce text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Scroll to features section"
            >
              <ChevronDown className="w-8 h-8" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-card/50 border-y border-border">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              EVERYTHING YOU NEED TO COACH LIKE A PRO
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Professional-grade tools designed to save you time and elevate your training sessions
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-background rounded-xl p-6 border border-border text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-2">DEDICATED MANAGER</h3>
              <p className="text-muted-foreground text-sm">A licenced coach assigned to assist clubs and individuals with platform upskilling via in person or web based support</p>
            </div>
            
            <div className="bg-background rounded-xl p-6 border border-border text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-2">AUSSIE FIRST PLANNING PLATFORM</h3>
              <p className="text-muted-foreground text-sm">Insights from A League, Academies to National Pathways</p>
            </div>
            
            <div className="bg-background rounded-xl p-6 border border-border text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Palette className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-2">SMOOTHEST DESIGNER</h3>
              <p className="text-muted-foreground text-sm">Create, edit and animate with our visual designer</p>
            </div>
            
            <div className="bg-background rounded-xl p-6 border border-border text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-2">CHEAPEST CLUB PACKAGES</h3>
              <p className="text-muted-foreground text-sm mb-3">Club based library tailored to your team's needs</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => scrollToSection("contact")}
                className="gap-1 text-xs"
              >
                Enquire Now
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
          
      {!subscribed && (
            <div className="text-center mt-10">
              <Link to="/auth">
                <Button size="lg" className="gap-2">
                  Get Started Now
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Drill Previews Section */}
      <section id="preview-drills" className="py-16 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-3">
              PREVIEW OUR DRILLS
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Professionally designed training exercises with detailed coaching points
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto mb-8">
            {previewDrills.map((drill) => (
              <DrillPreviewCard key={drill.id} drill={drill} />
            ))}
          </div>
          
          <div className="text-center">
            <Link to={subscribed ? "/library" : "/auth"}>
              <Button size="lg" className="gap-2">
                <Eye className="w-5 h-5" />
                {subscribed ? "View All 150+ Drills" : "See All 150+ Drills"}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>


      {/* About Section */}
      <section id="about" className="py-24 px-6">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-display text-4xl md:text-5xl text-foreground mb-6">
                  ABOUT ME
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Welcome to OZ Performance! I'm Brendan, a passionate football coach 
                    dedicated to helping coaches at all levels deliver better training sessions.
                  </p>
                  <p>
                    Most recently, I served as the U20 Women's Head Coach at Football Victoria 
                    Academy and the SSV Team Vic U12s Girls Head Coach. With years of experience 
                    in player development and tactical coaching, I've created this platform to 
                    share professional-grade training drills and session planning tools.
                  </p>
                  <p>
                    Whether you're coaching grassroots youth or semi-professional players, 
                    these resources are designed to save you time and elevate your sessions.
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-8 border border-primary/20">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full mx-auto mb-6 overflow-hidden border-2 border-primary/30">
                    <img 
                      src={brendanPhoto} 
                      alt="Brendan" 
                      className="w-full h-full object-cover scale-[1.42] translate-x-[1%]"
                      loading="lazy"
                      decoding="async"
                      width="96"
                      height="96"
                    />
                  </div>
                  <h3 className="font-display text-2xl text-foreground mb-2">Brendan</h3>
                  <p className="text-primary font-medium mb-4">Performance Coach</p>
                  <p className="text-muted-foreground text-sm">
                    Helping coaches deliver professional training sessions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-muted/30">
        <div className="container mx-auto">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl md:text-5xl text-foreground mb-4">
                GET IN TOUCH
              </h2>
              <p className="text-muted-foreground text-lg">
                Have questions or want to learn more? Send me a message directly.
              </p>
            </div>
            
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    Your Name
                  </label>
                  <Input
                    id="name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-background"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Your Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-background"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="How can I help you?"
                  rows={5}
                  className="bg-background resize-none"
                />
              </div>
              
              {!user && (
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="contact-human"
                    checked={isHuman}
                    onCheckedChange={(checked) => setIsHuman(checked === true)}
                  />
                  <label
                    htmlFor="contact-human"
                    className="text-sm text-muted-foreground cursor-pointer select-none"
                  >
                    I'm not a robot
                  </label>
                </div>
              )}
              
              <Button type="submit" size="lg" className="w-full gap-2" disabled={isSending || (!user && !isHuman)}>
                <Mail className="w-5 h-5" />
                {isSending ? "Sending..." : "Send Message"}
              </Button>
              
              <p className="text-center text-muted-foreground text-sm">
                Or email me directly at{" "}
                <a 
                  href="mailto:brendan.c@ozperformance.com.au" 
                  className="text-primary hover:underline"
                >
                  brendan.c@ozperformance.com.au
                </a>
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="font-display text-xl text-primary-foreground">OZ</span>
              </div>
              <span className="font-display text-lg text-foreground">OZ PERFORMANCE</span>
            </div>
            
            <nav className="flex items-center gap-8">
              <Link to="/library" className="text-muted-foreground hover:text-foreground transition-colors">
                Drills Library
              </Link>
              <Link to="/designer" className="text-muted-foreground hover:text-foreground transition-colors">
                Designer
              </Link>
              <Link to="/sessions" className="text-muted-foreground hover:text-foreground transition-colors">
                Sessions
              </Link>
            </nav>
            
            <p className="text-muted-foreground text-sm">
              © {new Date().getFullYear()} OZ Performance. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}